import { GetServerSideProps } from 'next'

import { Layout } from 'components/ui'
import { MetaTags } from 'components/shared'
import { addApolloState, initializeApollo } from 'services/apollo'
import webSections from 'components/sections'

import Main from 'components/auctions/main'
import {
  GetAuctionsQuery,
  GetAuctionsQueryVariables,
  GrantedWebSettingsQuery,
  GrantedWebSettingsQueryVariables,
} from 'gql'
import { Fragment } from 'react'
import EditSectionWrapper from 'components/home/block/edit-section-wrapper'
import { useGrantedWebSections } from 'hooks'
import { GRANTED_WEB_SETTINGS } from 'gql/platform/queries'
import { GET_AUCTIONS } from 'gql/artwork/queries'
import { PARTNER_SLUG } from 'constants/constant'

export const getServerSideProps: GetServerSideProps = async () => {
  const apolloClient = initializeApollo()

  const [{ data }, { data: sectionsData }] = await Promise.all([
    apolloClient.query<GrantedWebSettingsQuery, GrantedWebSettingsQueryVariables>({
      variables: { type: 'marketing-page', partnerSlug: PARTNER_SLUG },
      query: GRANTED_WEB_SETTINGS,
    }),
    apolloClient.query<GrantedWebSettingsQuery, GrantedWebSettingsQueryVariables>({
      variables: { name: 'auctions-page-sections', partnerSlug: PARTNER_SLUG },
      query: GRANTED_WEB_SETTINGS,
    }),
  ])

  const marketingPagesData = data.grantedWebSettings.edges
    .map(({ node }) => ({
      ...node,
      settings: JSON.parse(node.settings),
      sections: node.sections.map((section) => ({
        ...section,
        settings: JSON.parse(section.settings),
      })),
    }))
    .filter(({ settings }) => !settings.isPrivate && !settings.isGiveaway)

  let lotsIds = []

  marketingPagesData.forEach(({ sections }) => {
    sections.forEach(({ type, settings }) => {
      if (type === 'lots' && Array.isArray(settings?.auctions)) {
        lotsIds = [...lotsIds, ...settings.auctions]
      }
    })
  })

  const { data: lots } = await apolloClient.query<GetAuctionsQuery, GetAuctionsQueryVariables>({
    query: GET_AUCTIONS,
    variables: {
      ids: lotsIds.join(','),
      isEnded: false,
      partnerSlug: PARTNER_SLUG,
    },
  })

  const formattedMarketingPagesData = marketingPagesData.map((page) => ({
    ...page,
    sections: page.sections.map((section) => {
      if (section.type === 'lots' && Array.isArray(section.settings?.auctions)) {
        return {
          ...section,
          lots: lots.allArtworks.edges.filter(({ node }) =>
            section.settings.auctions.includes(node.id)
          ),
        }
      }

      return section
    }),
  }))

  return addApolloState(apolloClient, {
    props: {
      marketingPagesData: formattedMarketingPagesData
        .filter((page) => page.sections.some(({ type }) => type === 'lots'))
        .sort((a, b) => a.settings.order - b.settings.order),
      sectionsData,
      lots,
    },
  })
}

const Auctions = ({ marketingPagesData }) => {
  const { data } = useGrantedWebSections({
    variables: { name: 'auctions-page-sections', partnerSlug: PARTNER_SLUG },
  })

  return (
    <Layout>
      <MetaTags title="Auctions" />
      <Layout.Banner />
      <Layout.HomeHeader />
      <Main>
        {data.map(
          ({ settings, type, name }, index) =>
            webSections[type] && (
              <Fragment key={name}>
                <EditSectionWrapper
                  href={`/admin-v2/auctions-page-sections?name=${name}`}
                  name={name}
                >
                  {webSections[type](
                    {
                      ...settings,
                      ...(type === 'live-auctions' ? { data: marketingPagesData } : {}),
                    },
                    index
                  )}
                </EditSectionWrapper>
              </Fragment>
            )
        )}
      </Main>
      <Layout.Footer />
    </Layout>
  )
}

export default Auctions
